import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Spinner,
  UncontrolledDropdown,
} from "reactstrap";

import { ActivityReportData } from "./types";
import { cleanName } from "./format";

const ALL_TYPES = "";
const ALL_TYPES_LABEL = "All content types";

/**
 * The individual records people opened — contractors, contracts, assessments,
 * sanctions entries — ranked across every content type at once, or narrowed to
 * one. The rows for every type arrive with the report, so switching type is
 * instant and never disagrees with the filters at the top of the page.
 */
const MostViewedRecords = ({
  data,
  contentTypes,
  limit = 10,
  loading,
}: {
  data: ActivityReportData | null;
  contentTypes: string[];
  limit?: number;
  loading: boolean;
}) => {
  const [contentType, setContentType] = useState<string>(ALL_TYPES);

  const records = useMemo(() => data?.records || [], [data]);

  // Only offer types the report knows about, and mark the ones with nothing to
  // show so the choice is not a dead end.
  const options = useMemo(() => {
    const counts = new Map<string, number>();
    records.forEach((row) => {
      counts.set(row.contentType, (counts.get(row.contentType) || 0) + 1);
    });

    return [
      { value: ALL_TYPES, label: ALL_TYPES_LABEL, count: records.length },
      ...contentTypes.map((type) => ({
        value: type,
        label: type,
        count: counts.get(type) || 0,
      })),
    ];
  }, [contentTypes, records]);

  const rows = useMemo(
    () =>
      (contentType
        ? records.filter((row) => row.contentType === contentType)
        : records
      ).slice(0, limit),
    [records, contentType, limit]
  );

  const riskViews = useMemo(
    () => records.filter((row) => row.risk).length,
    [records]
  );

  const selectedLabel =
    options.find((option) => option.value === contentType)?.label ||
    ALL_TYPES_LABEL;

  return (
    <Col xxl={6}>
      <Card className="card-height-100">
        <CardHeader className="align-items-center d-flex">
          <div className="flex-grow-1">
            <h4 className="card-title mb-0">Most viewed records</h4>
            {!loading && riskViews > 0 ? (
              <span className="badge bg-danger-subtle text-ink-danger fs-12 mt-1">
                <i
                  className="ri-alarm-warning-line me-1"
                  aria-hidden="true"
                ></i>
                {riskViews} risk {riskViews === 1 ? "record" : "records"} viewed
              </span>
            ) : null}
          </div>
          <UncontrolledDropdown className="card-header-dropdown flex-shrink-0">
            <DropdownToggle
              tag="a"
              className="text-reset dropdown-btn"
              role="button"
            >
              <span
                className="text-ink-muted"
                aria-label={`Show: ${selectedLabel}`}
              >
                {selectedLabel}
                <i className="mdi mdi-chevron-down ms-1"></i>
              </span>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
              {options.map((option) => (
                <DropdownItem
                  key={option.value || "all"}
                  onClick={() => setContentType(option.value)}
                  className={contentType === option.value ? "active" : ""}
                >
                  {option.label}
                  {option.count === 0 ? (
                    <span className="text-ink-muted fs-11 ms-1">
                      (no views)
                    </span>
                  ) : null}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" size="sm" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-ink-muted py-5">
              {contentType
                ? `No ${selectedLabel} records were opened in this period.`
                : "No records were opened in this period."}
            </div>
          ) : (
            <div className="table-responsive table-card">
              <table className="table align-middle table-borderless table-nowrap mb-0">
                <thead className="text-ink-muted table-light">
                  <tr>
                    <th scope="col" style={{ width: "50%" }}>
                      Record
                    </th>
                    <th scope="col">Views</th>
                    <th scope="col">Users</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.contentType}-${row.entityId}`}>
                      <td>
                        <div
                          className="text-truncate"
                          title={cleanName(row.name)}
                        >
                          {row.pageUrl ? (
                            <Link to={row.pageUrl} className="fw-medium">
                              {cleanName(row.name)}
                            </Link>
                          ) : (
                            <span className="fw-medium">
                              {cleanName(row.name)}
                            </span>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {/* Redundant once the list is already one type. */}
                          {contentType ? null : (
                            <small className="text-ink-muted">
                              {row.contentType}
                            </small>
                          )}
                          {row.risk ? (
                            <span className="badge bg-danger-subtle text-ink-danger">
                              {row.risk === "sanctions"
                                ? "Sanctions list"
                                : "Flagged by RMU"}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>{row.visits}</td>
                      <td>{row.users}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default MostViewedRecords;
